import { zodResolver } from '@hookform/resolvers/zod';
import { FIELD_NAMES } from 'modules/projectSettings/constants';
import { ProjectLabel } from 'modules/projectSettings/types';
import { createPositiveToastMessage } from 'modules/projectSettings/utils';
import { addPositiveToast } from 'modules/toastContainer';
import { Controller, useController, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { BUTTON_SIZES, PrimaryButton } from 'ui/button';
import { FormField } from 'ui/formField';
import { InputText } from 'ui/inputs';
import { Modal, MODAL_SIZES, MODAL_VARIANTS, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { z } from 'zod';
import {
  EditLabelPayload,
  ProjectSettingsFormSchemaType,
  useProjectSettingsModalsContext,
} from '../../contexts';

const { LABELS } = FIELD_NAMES;

export const EditLabelModal = () => {
  const dispatch = useDispatch();
  const { hideModal, modalState } = useProjectSettingsModalsContext();
  const label = modalState?.payload as EditLabelPayload;

  const {
    field: { onChange, value: labels },
  } = useController<ProjectSettingsFormSchemaType>({
    name: LABELS,
  });

  const { control, handleSubmit } = useForm<{ newLabel: string }>({
    defaultValues: { newLabel: label?.name || '' },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: zodResolver(
      z.object({
        newLabel: z
          .string()
          .min(1, 'Name is required')
          .refine((name) => {
            const duplicateName = (labels as ProjectLabel[]).find(
              (label) => label.name === name?.trim(),
            );
            return !duplicateName;
          }, 'A label with the same name already exists in your project.'),
      }),
    ),
  });

  const onSubmit = ({ newLabel }: { newLabel: string }) => {
    const editedLabels = (labels as ProjectLabel[]).map((l) =>
      label.id === l.id ? { ...l, name: newLabel } : l,
    );
    onChange(editedLabels);
    const message = createPositiveToastMessage('Label', 'updated');
    dispatch(addPositiveToast(message));
    hideModal();
  };

  return (
    <Modal
      id="edit-new-label-modal"
      onClose={hideModal}
      isModalOpen
      size={MODAL_SIZES.SMALL}
      variant={MODAL_VARIANTS.DEFAULT}
      withPortal
    >
      <ModalHeader>Edit {label.name}</ModalHeader>
      <ModalBody>
        <form data-test="edit-label-form" id="edit-label-form" onSubmit={handleSubmit(onSubmit)}>
          <Controller
            control={control}
            name="newLabel"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <FormField
                inputId="name"
                labelText="Name"
                hasError={!!error?.message}
                errorText={error?.message}
              >
                <InputText id="name" value={value} onChange={onChange} autoFocus />
              </FormField>
            )}
          />
        </form>
      </ModalBody>
      <ModalFooter onClose={hideModal}>
        <PrimaryButton form="edit-label-form" size={BUTTON_SIZES.LARGE} type="submit">
          Save label
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};
